import identity from 'lodash/identity';
import isEmpty from 'lodash/isEmpty';
import isPlainObject from 'lodash/isPlainObject';
import type {
  Field,
  FieldCreator,
  FieldOptions,
  FieldCreatorOpts,
} from '../../types';

/**
 * Used to generate {@link FieldCreator}s for use in the {@link ParserConfig}s.
 *
 * @param {string} name - The name to be given to this field
 * @param {Object} opts - The options object used for constructing the field
 * @param {boolean} [opts.iterable] - Determines whether this field should be
 *     treated as an array
 * @param {boolean} [opts.nested] - If this field is an array or an object, it
 *     should be nested. This allows you to validate the inner properties and
 *     values as well.
 * @param {function(value: any): boolean} opts.validator - The function used to
 *     validate any default args and the data to be parsed.
 * @return {FieldCreator}
 */
export default function createField(
  name: string,
  { iterable = false, nested = false, validator }: FieldOptions,
): FieldCreator {
  // iterable fields are always nested
  nested = iterable ? true : nested;
  /**
   * You use field creators to populate your config. They are used for declaring
   * arbitrary default data (that validates) for various types. Eg., you can
   * use the string field creator multiple times with different default values
   * for each.
   *
   * See the lodash documentation {@see https://lodash.com/docs/4.17.2#get} for
   * more details about the path format.
   *
   * @param {FieldCreatorArg} arg - The default value for a field or a nested
   *     field or parser if this is a nested field.
   * @param {Object} [opts]
   * @param {string|array} [opts.path] - The arbitrary path to the data you want.
   *     Allows to move and rename fields. See the lodash documentation for more
   *     details on the path format.
   * @param {function} [opts.transform] - If provided, a value will be transformed
   *     before validation. Ie. `validate(transform(value))`. Useful coercing types.
   * @return {Field}
   *
   * @see https://lodash.com/docs/4.17.2#get
   */
  return function(arg: any, options?: FieldCreatorOpts): Field {
    const { path = '', transform = identity } = options || {};
    let isValid;

    if (!nested && !validator(arg)) {
      throw Error(
        `The default value '${arg}' for the field '${name}' doesn't pass the validator`,
      );
    }

    if (typeof transform !== 'function') {
      throw Error(`The transform for the field '${name}' must be a function.`);
    }

    if (!nested) {
      isValid = value => (validator(value) ? value : arg);
    } else if (nested && (typeof arg !== 'object' || isEmpty(arg))) {
      isValid = value => {
        if (iterable) {
          return Array.isArray(value) ? value : [];
        }
        return isPlainObject(value) ? value : {};
      };
    } else {
      switch (arg.type) {
        case 'field':
          isValid = arg.validate;
          break;
        case 'parser':
          isValid = arg.parse;
          break;
        default:
          throw Error(
            `Unrecognized nested field or parser of type: '${arg.type}'`,
          );
      }
    }

    const validate = value => isValid(transform(value));

    return Object.freeze({
      type: 'field',
      iterable,
      nested,
      path,
      name,
      validate,
    });
  };
}

/**
 * @typedef {string|number|boolean|Field|Parser} FieldCreatorArg
 */

/**
 * @typedef {Object} Field
 * @property {string} type This value is always 'field'
 * @property {boolean} iterable Will operate as if the value is an array.
 *     iterable fields are always nested.
 * @property {boolean} nested Indicated that there are validators or parsers
 *     to run inside this data (ie. Objects and Arrays).
 * @property {string|array|undefined} [path] The optional path to the data
 * @property {string} name The name given to this field
 * @property {function} validate The function that ensures the data is of
 *     the correct type. May actually be a nested parser or validator.
 */

/**
 * You use field creators to populate your config. They are used for declaring
 * arbitrary default data (that validates) for various types. Eg., you can
 * use the string field creator multiple times with different default values
 * for each.
 *
 * See the lodash documentation {@see https://lodash.com/docs/4.17.2#get} for
 * more details about the path format.
 *
 * @typedef FieldCreator
 * @param {FieldCreatorArg} arg - The default value for a field or a nested
 *     field or parser if this is a nested field.
 * @param {string|array} [path] - The arbitrary path to the data you want.
 *     Allows to move and rename fields. See the lodash documentation for more
 *     details on the path format.
 * @return {Field}
 *
 * @see https://lodash.com/docs/4.17.2#get
 */
